import {useMemo, useRef} from 'react'
import type {BaseTask} from '../types/task'
import {useQueries, useQuery} from '@github-ui/react-query'
import type {IdToHydratedPullRequestMap, IdToPullRequestMap} from '../types/pull'
import type {Diffstats} from '../types/diffstats'
import {SessionState} from '../types/session'
import {useLocalStorageCache} from './useLocalStorageCache'

export const PR_MAP_STORAGE_KEY = 'AGENT_SESSIONS_PR_MAP'

export function getSortedResourceIds(tasks: BaseTask[]): string {
  const ids = tasks ? tasks.map(task => task.resource_id) : []
  // Dedupe before sorting
  const uniqueIds = Array.from(new Set(ids))
  return uniqueIds.sort((a, b) => Number(a) - Number(b)).join(',')
}

export const fetchPullRequests = async (tasks: BaseTask[], setCachedPRMap: (prMap: IdToPullRequestMap) => void) => {
  const pullRequestIds = getSortedResourceIds(tasks)

  if (!pullRequestIds) {
    return {}
  }

  const response = await fetch(
    `/copilot/agent-sessions/pull-requests?pull_request_ids=${encodeURIComponent(pullRequestIds)}`,
    {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    },
  )

  if (!response.ok) {
    throw new Error('Failed to fetch pull requests')
  }

  const data = await response.json()
  const pullRequests = data.pull_requests

  // Cache the pull requests to localStorage
  setCachedPRMap(pullRequests)

  return pullRequests
}

// Fetches diffstat for a single pull request given its PR id
async function fetchDiffstatData(id: number, url: string): Promise<{id: number; diffstat: Diffstats | null}> {
  if (!url) {
    return {id, diffstat: null}
  }
  const urlToParse = new URL(url, window.location.origin)
  const pathAndQuery = urlToParse.pathname + urlToParse.search + urlToParse.hash
  const response = await fetch(`${pathAndQuery}/page_data/diffstat`, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })
  if (!response.ok) throw new Error(`Failed to fetch diffstat for PR ${id}`)
  const json = await response.json()
  const diffstat: Diffstats = {
    lines_added: json.diffstat.linesAdded ?? 0,
    lines_deleted: json.diffstat.linesDeleted ?? 0,
    lines_changed: json.diffstat.linesChanged ?? 0,
  }
  return {id, diffstat}
}

export function usePullRequests({
  tasks,
  sessionsPollingInterval = 3000,
  initialPullRequestsMap,
}: {
  tasks: BaseTask[]
  sessionsPollingInterval?: number | false
  initialPullRequestsMap?: IdToPullRequestMap
}) {
  const pullRequestIds = getSortedResourceIds(tasks)
  const {value: cachedPRMap, setValue: setCachedPRMap} = useLocalStorageCache<IdToPullRequestMap>(PR_MAP_STORAGE_KEY)

  // The ref will hold our cumulative data to prevent flicker
  const cumulativePullsRef = useRef<IdToPullRequestMap>(initialPullRequestsMap ?? {})

  const pullRequestsQuery = useQuery<IdToPullRequestMap>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['agent-sessions-pull-requests', pullRequestIds],
    queryFn: async () => await fetchPullRequests(tasks, setCachedPRMap),
    initialData: initialPullRequestsMap ?? cachedPRMap,
    refetchInterval: sessionsPollingInterval,
    refetchIntervalInBackground: false,
    enabled: pullRequestIds.length > 0,
  })

  const taskByResourceId: Record<number, BaseTask> = {}

  for (const task of tasks) {
    if (task.resource_id !== undefined) {
      taskByResourceId[task.resource_id] = task
    }
  }

  const prList = pullRequestsQuery.data ? Object.values(pullRequestsQuery.data) : []
  const diffstatQueries = useQueries({
    queries: prList.map(pr => ({
      queryKey: ['diffstat', pr.id, pr.url],
      queryFn: () => fetchDiffstatData(pr.id, pr.url),
      enabled: taskByResourceId[pr.id]?.latest_session_state === SessionState.Completed,
    })),
  })

  // Build a map from PR id to diffstat
  const diffstatMap: Record<number, Diffstats | undefined> = {}
  for (const query of diffstatQueries) {
    if (query.data) {
      diffstatMap[query.data.id] = query.data.diffstat ?? undefined
    }
  }

  // When new data comes in, merge it into our cumulative ref
  if (pullRequestsQuery.data) {
    // Combine pull requests with their diffstats by id
    const hydratedPullRequestsMap: IdToHydratedPullRequestMap = {}
    for (const pr of Object.values(pullRequestsQuery.data)) {
      hydratedPullRequestsMap[pr.id] = {
        ...pr,
        diffstat: diffstatMap[pr.id],
      }
    }
    cumulativePullsRef.current = {
      ...cumulativePullsRef.current,
      ...hydratedPullRequestsMap,
    }
  }

  // We use a memo to ensure our component re-renders when the query is done,
  // returning the complete, cumulative data from the ref.
  const finalMap = useMemo(() => {
    // This memo depends on the query's data. When the fetch completes,
    // query.data changes, this recalculates, and the component re-renders
    // with the full map from the ref.
    return cumulativePullsRef.current
    // eslint-disable-next-line react-hooks/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pullRequestsQuery.data, diffstatMap])

  return {
    ...pullRequestsQuery,
    // Always return the cumulative data to prevent the UI from ever emptying out in transitional states
    data: finalMap,
  }
}
